import React, { useReducer, useState } from 'react';

import { SlCheckbox } from '@shoelace-style/shoelace/dist/react';

import { translate } from '~l10n/client/translate';
import { SmallButton } from '~shared/components/buttons';
import { SelectNumberRadioGroup } from '~shared/components/SelectNumberRadio/SelectNumberRadio';
import { Spinner } from '~shared/components/Spinner';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { InformationIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/InformationIcon';
import { EightyPercentQuotaInlineMessage } from '~teacher/components/QuotaInlineMessage/EightyPercentQuotaInlineMessage';
import { HundredPercentQuotaInlineMessage } from '~teacher/components/QuotaInlineMessage/HundredPercentQuotaInlineMessage';
import { GenerateQuestionDialogContent } from '~teacher/components/ResultsGrid/Insights/GenerateQuestions/Layout/GenerateQuestionDialogContent';
import { useAiQuotaWarning } from '~teacher/hooks/useAiQuotaWarning';
import { QuizQuestion } from '~teacher/models/quizzes';

interface SelectAreasProps extends SelectAreasState {
  generateQuestions: () => void;
  backToGenerationType?: () => void;
  isGeneratingQuestions: boolean;
  insights: Array<{
    description: string;
    topic: string;
    index: number;
    questionIds: Array<string>;
  }>;
  dispatch: SelectAreasDispatch;
  questions: Record<string, QuizQuestion>;
  generateExplanations: boolean;
}

export function SelectAreas({
  backToGenerationType,
  generateQuestions,
  isGeneratingQuestions,
  insightIndexes,
  questionCount,
  dispatch,
  insights,
  questions,
  generateExplanations,
}: SelectAreasProps) {
  const {
    level: quotaLevel,
    resetDate,
    percentUsed,
  } = useAiQuotaWarning('Generate');
  const [warningDismissed, setWarningDismissed] = useState(false);

  return (
    <GenerateQuestionDialogContent
      header={
        <GenerateQuestionDialogContent.Header
          title={translate('Generate from Areas of Improvement')}
          description={translate(
            'Select Areas of Improvement to include in the assessment.'
          )}
          onBack={backToGenerationType}
        />
      }
      footer={
        <GenerateQuestionDialogContent.Footer>
          <div
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
            className="flex justify-between items-center gap-2 p-5"
          >
            <div className="flex flex-row items-center gap-2">
              <label
                className="text-base text-blue-900"
                htmlFor="generate-explanations-toggle"
                id="generate-explanations-label"
              >
                {translate('Generate Explanations')}{' '}
              </label>

              <span id="generate-explanations-description" className="sr-only">
                {translate(
                  'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
                )}
              </span>

              <TooltipV2
                mode="click"
                contentWidth={260}
                label={translate(
                  'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
                )}
              >
                <InformationIcon />
              </TooltipV2>
            </div>
            <ToggleSwitch
              id="generate-explanations-toggle"
              size="small"
              on={generateExplanations}
              aria-labelledby="generate-explanations-label"
              aria-describedby="generate-explanations-description"
              onChange={(value) => {
                dispatch({
                  type: 'SET_GENERATE_EXPLANATIONS',
                  payload: { value },
                });
              }}
            />
          </div>
          <div
            className="p-5 flex flex-col gap-4"
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
          >
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="flex flex-row items-center gap-2">
                <label
                  htmlFor="number-of-questions"
                  className="text-base text-blue-900 leading-8"
                  id="number-of-questions-label"
                >
                  {translate('Number of Questions')}
                </label>

                <span id="number-of-questions-description" className="sr-only">
                  {translate(
                    'All areas covered, giving more questions to the biggest gaps.'
                  )}
                </span>

                <TooltipV2
                  mode="click"
                  contentWidth={260}
                  label={translate(
                    'All areas covered, giving more questions to the biggest gaps.'
                  )}
                >
                  <InformationIcon />
                </TooltipV2>
              </div>
              <SelectNumberRadioGroup
                aria-labelledby="number-of-questions-label"
                aria-describedby="number-of-questions-description"
                id="number-of-questions"
                name="numberOfQuestions"
                selectedNumber={questionCount}
                numberOptions={[5, 10, 15]}
                onChange={(number) => {
                  dispatch({
                    type: 'SET_QUESTION_COUNT',
                    payload: { value: number },
                  });
                }}
              >
                <span slot="label" className="sr-only"></span>
              </SelectNumberRadioGroup>
            </div>
          </div>
          <div
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
            className="flex justify-end items-center h-24 gap-4 py-5 px-5"
          >
            {quotaLevel === 'warning' && !warningDismissed ? (
              <EightyPercentQuotaInlineMessage
                bucket="Generate"
                upgradeHref="#pricing"
                onClose={() => setWarningDismissed(true)}
                percentUsed={percentUsed}
              />
            ) : null}
            {quotaLevel === 'exceeded' ? (
              <HundredPercentQuotaInlineMessage
                bucket="Generate"
                upgradeHref="#pricing"
                limitResetDate={resetDate}
                percentUsed={percentUsed}
              />
            ) : null}
            <div>
              <SmallButton
                appearance="primary"
                isDisabled={
                  insightIndexes.size === 0 || quotaLevel === 'exceeded'
                }
                isLoading={isGeneratingQuestions}
                onClick={generateQuestions}
              >
                {translate('Generate')}
              </SmallButton>
            </div>
          </div>
        </GenerateQuestionDialogContent.Footer>
      }
    >
      <div className="flex flex-col gap-4 pt-5">
        {insights.map(({ topic, description, index, questionIds }) => {
          const insightQuestions = questionIds
            .map((questionId) => questions[questionId])
            .filter(Boolean);
          return (
            <div className="flex items-start gap-4" key={index}>
              <div className="py-3">
                <SlCheckbox
                  key={index}
                  role="checkbox"
                  checked={insightIndexes.has(index)}
                  disabled={isGeneratingQuestions}
                  onSlChange={(e) => {
                    if (isGeneratingQuestions) return;
                    const target = e.target as HTMLInputElement;
                    if (target.checked) {
                      dispatch({
                        type: 'ADD_INSIGHT_INDEX',
                        payload: { value: index },
                      });
                    } else {
                      dispatch({
                        type: 'REMOVE_INSIGHT_INDEX',
                        payload: { value: index },
                      });
                    }
                  }}
                >
                  <span className="sr-only">{topic}</span>
                </SlCheckbox>
              </div>
              <div>
                <p className="font-sans text-base leading-8 font-600">
                  {topic} (
                  {insightQuestions.map((q) => `Q${q.order}`).join(', ')})
                </p>
                <p className="font-sans text-base leading-8">{description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </GenerateQuestionDialogContent>
  );
}

interface SelectAreasLoadingProps {
  backToGenerationType: () => void;
}

export function SelectAreasLoading({
  backToGenerationType,
}: SelectAreasLoadingProps) {
  return (
    <GenerateQuestionDialogContent
      header={
        <GenerateQuestionDialogContent.Header
          onBack={backToGenerationType}
          title={translate('Generate from Areas of Improvement')}
          description={translate(
            'Select Areas of Improvement to include in the assessment.'
          )}
        />
      }
      footer={
        <GenerateQuestionDialogContent.Footer>
          <div
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
            className="flex justify-between items-center gap-2 p-5"
          >
            <div className="flex flex-row items-center gap-2">
              <label
                className="text-base text-blue-900"
                htmlFor="generate-explanations-toggle"
                id="generate-explanations-label"
              >
                {translate('Generate Explanations')}{' '}
              </label>

              <span id="generate-explanations-description" className="sr-only">
                {translate(
                  'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
                )}
              </span>

              <TooltipV2
                mode="click"
                contentWidth={260}
                label={translate(
                  'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
                )}
              >
                <InformationIcon />
              </TooltipV2>
            </div>

            <ToggleSwitch
              disabled
              id="generate-explanations-toggle"
              size="small"
              on={true}
              labelledBy="generate-explanations-label"
              describedBy="generate-explanations-description"
              onChange={() => {}}
            />
          </div>
          <div
            className="p-5 flex flex-col gap-4"
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
          >
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="flex flex-row items-center gap-2">
                <label
                  htmlFor="number-of-questions"
                  className="text-base text-blue-900 leading-8"
                  id="number-of-questions-label"
                >
                  {translate('Number of Questions')}
                </label>

                <span id="number-of-questions-description" className="sr-only">
                  {translate(
                    'All areas covered, giving more questions to the biggest gaps.'
                  )}
                </span>

                <TooltipV2
                  mode="click"
                  contentWidth={260}
                  label={translate(
                    'All areas covered, giving more questions to the biggest gaps.'
                  )}
                >
                  <InformationIcon />
                </TooltipV2>
              </div>
              <SelectNumberRadioGroup
                id="number-of-questions"
                disabled
                aria-labelledby="number-of-questions-label"
                aria-describedby="number-of-questions-description"
                name="numberOfQuestions"
                selectedNumber={5}
                numberOptions={[5, 10, 15]}
                onChange={() => {}}
              />
            </div>
          </div>
          <div
            style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
            className="flex justify-end p-5"
          >
            <SmallButton appearance="primary" isDisabled onClick={() => {}}>
              {translate('Generate')}
            </SmallButton>
          </div>
        </GenerateQuestionDialogContent.Footer>
      }
    >
      <div className="flex justify-center items-center h-full">
        <Spinner />
      </div>
    </GenerateQuestionDialogContent>
  );
}

type SelectAreasState = {
  isGeneratingQuestions: boolean;
  questionCount: number;
  insightIndexes: Set<number>;
  generateExplanations: boolean;
};

type SelectAreasAction =
  | {
      type: 'START_GENERATING_QUESTIONS';
    }
  | {
      type: 'STOP_GENERATING_QUESTIONS';
    }
  | {
      type: 'SET_GENERATE_EXPLANATIONS';
      payload: { value: boolean };
    }
  | {
      type: 'SET_QUESTION_COUNT';
      payload: { value: number };
    }
  | {
      type: 'SET_INSIGHT_INDEXES';
      payload: { value: Set<number> };
    }
  | {
      type: 'ADD_INSIGHT_INDEX';
      payload: { value: number };
    }
  | {
      type: 'REMOVE_INSIGHT_INDEX';
      payload: { value: number };
    }
  | {
      type: 'RESET';
    };

const initialState: SelectAreasState = {
  isGeneratingQuestions: false,
  questionCount: 5,
  insightIndexes: new Set(),
  generateExplanations: true,
};

function reducer(
  state: SelectAreasState,
  action: SelectAreasAction
): SelectAreasState {
  switch (action.type) {
    case 'START_GENERATING_QUESTIONS':
      return { ...state, isGeneratingQuestions: true };
    case 'STOP_GENERATING_QUESTIONS':
      return { ...state, isGeneratingQuestions: false };
    case 'SET_QUESTION_COUNT':
      return { ...state, questionCount: action.payload.value };
    case 'SET_INSIGHT_INDEXES':
      return { ...state, insightIndexes: action.payload.value };
    case 'ADD_INSIGHT_INDEX':
      return {
        ...state,
        insightIndexes: new Set([
          ...state.insightIndexes,
          action.payload.value,
        ]),
      };
    case 'REMOVE_INSIGHT_INDEX':
      return {
        ...state,
        insightIndexes: new Set(
          [...state.insightIndexes].filter((id) => id !== action.payload.value)
        ),
      };
    case 'SET_GENERATE_EXPLANATIONS':
      return { ...state, generateExplanations: action.payload.value };
    case 'RESET':
      return { ...initialState, insightIndexes: new Set() };
    default:
      return state;
  }
}

export function useSelectAreasReducer() {
  return useReducer(reducer, initialState);
}

export type SelectAreasDispatch = React.Dispatch<SelectAreasAction>;
